import { Pagination, Stack } from "@mui/material";
import { memo, ReactNode, useCallback, useEffect, useState } from "react";

import { IExams } from "../../models/exams";
import ExamItem from "./ExamItem";
import { useExtensionConfigs } from "../../hooks/useExtensionConfigs";
import { Extension } from "../../models/intalled-extensions";
import { IExamsWithPacsValidation } from "../../components/Exams/ExamPacsAction/models";

interface IExamsListProps {
  data: IExams[] | IExamsWithPacsValidation[];
  page: number;
  onPageChange: (page: number) => void;
  count: number;
  individualId?: string;
  hasPixeonExtension: boolean;
  hasVueMotionExtension: boolean;
  hasExamPacsAction: boolean;
  loadingPacsValues: boolean;
  pacsColumnName: string | null;
  renderActions?: (exam: IExams | IExamsWithPacsValidation) => ReactNode;
  /** Quando true, respeita ShowImagesColumn/ImagesColumnTitle da extensão de exames */
  useImagesColumnConfig?: boolean;
}

const ExamsList: React.FC<IExamsListProps> = ({
  data,
  page,
  onPageChange,
  count,
  individualId,
  hasPixeonExtension,
  hasVueMotionExtension,
  hasExamPacsAction,
  loadingPacsValues,
  pacsColumnName,
  renderActions,
  useImagesColumnConfig = false,
}) => {
  const [cdnUrl, setCdnUrl] = useState<boolean>(false);
  // Com a config ativa, o botão só aparece depois de carregar (evita piscar)
  const [showImagesColumn, setShowImagesColumn] = useState<boolean>(
    !useImagesColumnConfig,
  );
  const [imagesColumnTitle, setImagesColumnTitle] = useState<string | null>(
    null,
  );
  const [checkPdfSerialOnNullFormat, setCheckPdfSerialOnNullFormat] =
    useState<boolean>(false);
  const { fetchExtensionConfig } = useExtensionConfigs();

  const fetchExtensions = useCallback(async () => {
    const configCdnUrl = await fetchExtensionConfig(Extension.EXAMS, "CDNURL");
    if (!configCdnUrl || configCdnUrl.value === ".") {
      setCdnUrl(false);
    } else {
      setCdnUrl(true);
    }

    const configCheckPdfSerialOnNullFormat = await fetchExtensionConfig(
      Extension.EXAMS,
      "CheckPdfSerialOnNullFormat",
    );
    setCheckPdfSerialOnNullFormat(
      configCheckPdfSerialOnNullFormat?.value === "1",
    );

    if (!useImagesColumnConfig) return;

    const configImagesColumnTitle = await fetchExtensionConfig(
      Extension.EXAMS,
      "ImagesColumnTitle",
    );
    setImagesColumnTitle(configImagesColumnTitle?.value?.trim() || null);

    const configShowImagesColumn = await fetchExtensionConfig(
      Extension.EXAMS,
      "ShowImagesColumn",
    );
    // Sem a config, o botão continua visível
    setShowImagesColumn(
      !configShowImagesColumn ||
        configShowImagesColumn.value?.trim().toLowerCase() !== "false",
    );
  }, [fetchExtensionConfig, useImagesColumnConfig]);

  useEffect(() => {
    fetchExtensions();
  }, [fetchExtensions]);

  return (
    <Stack gap={2} mt={2}>
      {data.map((exam) => (
        <ExamItem
          key={exam.id}
          data={exam}
          individualId={individualId}
          cdnUrl={cdnUrl}
          checkPdfSerialOnNullFormat={checkPdfSerialOnNullFormat}
          hasPixeonExtension={hasPixeonExtension}
          hasVueMotionExtension={hasVueMotionExtension}
          hasExamPacsAction={hasExamPacsAction}
          loadingPacsValues={loadingPacsValues}
          pacsColumnName={pacsColumnName}
          renderActions={renderActions}
          showImagesColumn={showImagesColumn}
          imagesColumnTitle={imagesColumnTitle}
        />
      ))}
      <Pagination
        count={count}
        page={page}
        sx={{ alignSelf: "center" }}
        onChange={(_, page) => onPageChange(page)}
      />
    </Stack>
  );
};

export default memo(ExamsList);
